import axiosInstance from "../utils/axios";

export interface News {
  id: number;
  title: string;
  shortDescription: string;
  longDescription: string;
  active: boolean;
  newsCategoryId?: number;
  categoryTitle?: string;
}

export interface NewsCategory {
  id: number;
  title: string;
  description?: string;
  active: boolean;
  displayOrder: number;
}

export interface GroupedNews {
  title: string;
  description?: string;
  categoryId: number;
  displayOrder: number;
  news: News[];
}

export interface Podcast {
  id: number;
  title: string;
  shortDescription: string;
  longDescription: string;
  imageUrl?: string;
  active: boolean;
  podcastTypeId: number;
  podcastTypeName?: string;
  isDownloadable?: boolean;
  timeSlots?: PodcastTimeSlot[];
}

export interface PodcastType {
  id: number;
  title: string;
  description: string;
  active: boolean;
}

export interface Settings {
  id: number;
  radioUrl: string;
  about: string;
  siteUrl: string;
  phoneNumber: string;
  baseUrl: string;
  faceBookUrl: string;
  instagramUrl: string;
  youtubeUrl: string;
  appIconPath: string;
  sideMenuBackgroundPath: string;
  dashboardBackgroundPath: string;
  aboutUsBackgroundPath: string;
  primaryColor: string;
  secondaryColor: string;
  textColorOnPrimary: string;
  textColorOnSecondary: string;
}
export interface PodcastEpisode {
  id: number;
  title: string;
  shortDescription: string;
  longDescription: string;
  audioUrl: string;
  active: boolean;
  episodeNumber: number;
  podcastId: number;
  audioDuration: string;
  startTime?: string;
  endTime?: string;
  scheduleDays?: string;
}

export interface PodcastTimeSlot {
  id?: number;
  podcastId?: number;
  podcastEpisodeId?: number;
  startTime: string;
  endTime: string;
  dayOfWeek: string | number;
}

export interface PaginatedResponse<T> {
  items: T[];
  totalItems: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

const API_VERSION = "v1.0";

// News API calls
export const fetchNews = async (): Promise<News[]> => {
  const response = await axiosInstance.get<News[]>(`/api/${API_VERSION}/News`);
  return response.data;
};

export const fetchNewsById = async (id: number): Promise<News> => {
  const response = await axiosInstance.get<News>(
    `/api/${API_VERSION}/News/${id}`
  );
  return response.data;
};

export const createNews = async (newsData: Omit<News, "id">): Promise<News> => {
  const response = await axiosInstance.post<News>(
    `/api/${API_VERSION}/News`,
    newsData
  );
  return response.data;
};

export const updateNews = async (newsData: News): Promise<News> => {
  const response = await axiosInstance.put<News>(
    `/api/${API_VERSION}/News/${newsData.id}`,
    newsData
  );
  return response.data;
};

export const deleteNews = async (id: number): Promise<void> => {
  await axiosInstance.delete(`/api/${API_VERSION}/News/${id}`);
};

// Grouped News API calls
export const fetchGroupedNews = async (): Promise<GroupedNews[]> => {
  const response = await axiosInstance.get<GroupedNews[]>(
    `/api/${API_VERSION}/News/grouped`
  );
  return response.data;
};

// News Category API calls
export const fetchNewsCategories = async (): Promise<NewsCategory[]> => {
  const response = await axiosInstance.get<NewsCategory[]>(
    `/api/${API_VERSION}/NewsCategory`
  );
  return response.data;
};

export const fetchNewsCategoryById = async (
  id: number
): Promise<NewsCategory> => {
  const response = await axiosInstance.get<NewsCategory>(
    `/api/${API_VERSION}/NewsCategory/${id}`
  );
  return response.data;
};

export const createNewsCategory = async (
  categoryData: Omit<NewsCategory, "id">
): Promise<NewsCategory> => {
  const response = await axiosInstance.post<NewsCategory>(
    `/api/${API_VERSION}/NewsCategory`,
    categoryData
  );
  return response.data;
};

export const updateNewsCategory = async (
  categoryData: NewsCategory
): Promise<NewsCategory> => {
  const response = await axiosInstance.put<NewsCategory>(
    `/api/${API_VERSION}/NewsCategory/${categoryData.id}`,
    categoryData
  );
  return response.data;
};

export const deleteNewsCategory = async (id: number): Promise<void> => {
  await axiosInstance.delete(`/api/${API_VERSION}/NewsCategory/${id}`);
};

// Podcast API calls
export const fetchPodcasts = async (
  pageNumber: number = 1,
  pageSize: number = 10
): Promise<PaginatedResponse<Podcast>> => {
  try {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });

    const response = await axiosInstance.get<PaginatedResponse<Podcast>>(
      `/api/${API_VERSION}/Podcast?${params.toString()}`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching podcasts:", error);
    throw error;
  }
};

export const fetchPodcastById = async (id: number): Promise<Podcast> => {
  try {
    const response = await axiosInstance.get<Podcast>(
      `/api/${API_VERSION}/Podcast/${id}`
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching podcast with ID ${id}:`, error);
    throw error;
  }
};

export const createPodcast = async (formData: FormData): Promise<Podcast> => {
  try {
    console.log("Creating podcast with FormData");

    const response = await axiosInstance.post<Podcast>(
      `/api/${API_VERSION}/Podcast`,
      formData
    );
    return response.data;
  } catch (error: any) {
    console.error("Error creating podcast:", error);
    if (error.response && error.response.data) {
      console.error("API error details:", error.response.data);
    }
    throw error;
  }
};

export const updatePodcast = async (
  params: { id: number; formData?: FormData } | Podcast
): Promise<Podcast> => {
  try {
    const id = "id" in params ? params.id : (params as Podcast).id;
    let formData: FormData;

    if ("formData" in params && params.formData) {
      formData = params.formData;
    } else {
      // Create FormData object for file uploads
      formData = new FormData();

      // Add all properties from podcastData to FormData
      const podcastData = params as Podcast;
      Object.entries(podcastData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      });
    }

    const response = await axiosInstance.put<Podcast>(
      `/api/${API_VERSION}/Podcast/${id}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  } catch (error: any) {
    const id = "id" in params ? params.id : (params as Podcast).id;
    console.error(`Error updating podcast with ID ${id}:`, error);
    if (error.response && error.response.data) {
      console.error("API error details:", error.response.data);
    }
    throw error;
  }
};

export const deletePodcast = async (id: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/api/${API_VERSION}/Podcast/${id}`);
  } catch (error: any) {
    console.error(`Error deleting podcast with ID ${id}:`, error);
    throw error;
  }
};

// Settings API calls
export const fetchSettings = async (): Promise<Settings> => {
  const response = await axiosInstance.get<Settings>(
    `/api/${API_VERSION}/Settings`
  );
  return response.data;
};

export const updateSettings = async (
  settingsData: FormData
): Promise<Settings> => {
  const response = await axiosInstance.put<Settings>(
    `/api/${API_VERSION}/Settings`,
    settingsData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
};

export const fetchPodcastTypes = async (): Promise<PodcastType[]> => {
  const response = await axiosInstance.get<PodcastType[]>(
    `/api/${API_VERSION}/PodcastType`
  );
  return response.data;
};

export const fetchPodcastTypeById = async (
  id: number
): Promise<PodcastType> => {
  const response = await axiosInstance.get<PodcastType>(
    `/api/${API_VERSION}/PodcastType/${id}`
  );
  return response.data;
};

export const createPodcastType = async (
  podcastTypeData: Omit<PodcastType, "id">
): Promise<PodcastType> => {
  const response = await axiosInstance.post<PodcastType>(
    `/api/${API_VERSION}/PodcastType`,
    podcastTypeData
  );
  return response.data;
};

export const updatePodcastType = async (
  podcastTypeData: PodcastType
): Promise<PodcastType> => {
  const response = await axiosInstance.put<PodcastType>(
    `/api/${API_VERSION}/PodcastType/${podcastTypeData.id}`,
    podcastTypeData
  );
  return response.data;
};

export const deletePodcastType = async (id: number): Promise<void> => {
  await axiosInstance.delete(`/api/${API_VERSION}/PodcastType/${id}`);
};

// PodcastEpisode API calls
export const fetchPodcastEpisodes = async (
  pageNumber: number = 1,
  pageSize: number = 10,
  podcastId?: number
): Promise<PaginatedResponse<PodcastEpisode>> => {
  try {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });

    if (podcastId) {
      params.append("podcastId", podcastId.toString());
    }

    const response = await axiosInstance.get<PaginatedResponse<PodcastEpisode>>(
      `/api/${API_VERSION}/PodcastEpisode?${params.toString()}`
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching podcast episodes:", error);
    throw error;
  }
};

export const fetchPodcastEpisodeById = async (
  id: number
): Promise<PodcastEpisode> => {
  try {
    const response = await axiosInstance.get<PodcastEpisode>(
      `/api/${API_VERSION}/PodcastEpisode/${id}`
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching podcast episode with ID ${id}:`, error);
    throw error;
  }
};

export const createPodcastEpisode = async (
  formData: FormData
): Promise<PodcastEpisode> => {
  try {
    // Log the form data for debugging, but don't log the actual file content
    const formDataKeys = Array.from(formData.keys());
    console.log(
      "Creating podcast episode with form data keys:",
      formDataKeys.reduce((obj: { [key: string]: any }, key) => {
        obj[key] =
          key === "audioFile" ? "[File content omitted]" : formData.get(key);
        return obj;
      }, {})
    );

    const response = await axiosInstance.post<PodcastEpisode>(
      `/api/${API_VERSION}/PodcastEpisode`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error creating podcast episode:", error);
    if (error.response && error.response.data) {
      console.error("API error details:", error.response.data);
    }
    throw error;
  }
};

export const updatePodcastEpisode = async (
  id: number,
  formData: FormData
): Promise<PodcastEpisode> => {
  try {
    // Log the form data for debugging, but don't log the actual file content
    const formDataKeys = Array.from(formData.keys());
    console.log(
      `Updating podcast episode ${id} with form data keys:`,
      formDataKeys.reduce((obj: { [key: string]: any }, key) => {
        obj[key] =
          key === "audioFile" ? "[File content omitted]" : formData.get(key);
        return obj;
      }, {})
    );

    const response = await axiosInstance.put<PodcastEpisode>(
      `/api/${API_VERSION}/PodcastEpisode/${id}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error updating podcast episode with ID ${id}:`, error);
    if (error.response && error.response.data) {
      console.error("API error details:", error.response.data);
    }
    throw error;
  }
};

export const deletePodcastEpisode = async (id: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/api/${API_VERSION}/PodcastEpisode/${id}`);
  } catch (error: any) {
    console.error(`Error deleting podcast episode with ID ${id}:`, error);
    throw error;
  }
};

// Add a new function to get the full URL for image paths
export const getImageUrl = (imagePath: string): string => {
  return `${axiosInstance.defaults.baseURL}uploads/${imagePath}`;
};
